import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Do you offer free estimates?",
      answer: "Yes! We provide free, no-obligation estimates for all projects. Simply fill out our quote request form or give us a call, and we'll schedule a time to assess your project and provide a detailed estimate."
    },
    {
      question: "What areas do you serve?",
      answer: "We proudly serve the Greater Calgary area and surrounding communities. Contact us to confirm if we service your specific location."
    },
    {
      question: "How long does a typical drywall project take?",
      answer: "Project timelines vary depending on the size and scope. A standard residential room can take 2-5 days including installation, taping, mudding, and finishing. Commercial projects are assessed individually. We'll provide a clear timeline during your estimate."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Absolutely! Sat Drywall Ltd is fully licensed and insured. We carry comprehensive liability insurance and WCB coverage to protect both our team and your property."
    },
    {
      question: "What's the difference between spray foam and traditional insulation?",
      answer: "Spray foam insulation provides superior air sealing and R-value compared to traditional batt insulation. It expands to fill gaps and cracks, creating an excellent moisture and air barrier. While it has a higher upfront cost, spray foam offers better long-term energy savings and performance."
    },
    {
      question: "Do you handle both commercial and residential projects?",
      answer: "Yes! We have extensive experience with both commercial and residential drywall and insulation projects. From small home repairs to large commercial buildings, we have the expertise and equipment to handle projects of any size."
    },
    {
      question: "How much does drywall installation cost?",
      answer: "Drywall costs vary based on project size, ceiling height, finish level, and complexity. On average, expect $2-4 per square foot for basic installation and finishing. Contact us for a free, accurate estimate tailored to your specific project."
    },
    {
      question: "What types of drywall finishes do you offer?",
      answer: "We offer all standard drywall finish levels from Level 1 (fire taping) through Level 5 (smooth, glass-like finish). We'll recommend the appropriate finish level based on your project requirements and budget."
    },
    {
      question: "Can you match existing textures for repairs?",
      answer: "Yes! We're experienced in matching various textures including orange peel, knockdown, and popcorn ceilings. We'll work to ensure repairs blend seamlessly with your existing walls or ceilings."
    },
    {
      question: "How soon can you start my project?",
      answer: "Our availability varies depending on the season and current project schedule. Typically, we can start most projects within 1-2 weeks of your estimate approval. For urgent repairs, we'll do our best to accommodate your timeline."
    },
    {
      question: "Do you offer financing options?",
      answer: "Please contact us directly to discuss payment options and scheduling that works for your budget. We're happy to work with you to find a solution that fits your needs."
    },
    {
      question: "What should I do to prepare for drywall installation?",
      answer: "We recommend clearing the work area of furniture and valuables, covering items that can't be moved, and ensuring clear access to the workspace. We'll provide specific preparation instructions based on your project during the estimate."
    },
    {
      question: "Do you clean up after the project?",
      answer: "Yes! We take pride in leaving your space clean. We'll remove all drywall debris, dust, and materials. While drywall work does create fine dust, we use dust containment measures and thoroughly clean the work area upon completion."
    },
    {
      question: "What's your warranty or guarantee?",
      answer: "We stand behind our work with a satisfaction guarantee. If you're not happy with the quality of our workmanship, we'll make it right. We also warranty our work against defects in materials and workmanship."
    },
    {
      question: "Can you work around my schedule?",
      answer: "We do our best to accommodate your schedule, including evening and weekend work when possible. Let us know your constraints during the estimate, and we'll work with you to find a suitable schedule."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1733431772808-82d878e59000?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxkcnl3YWxsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3NDQ4Mjc2Mnww&ixlib=rb-4.1.0&q=85"
            alt="FAQ Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Got questions? We've got answers! Find everything you need to know about our drywall and insulation services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 hover:border-[#00AEEF] transition-colors duration-300"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Can't find the answer you're looking for? Our team is here to help! Contact us and we'll get back to you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-[#00AEEF] hover:bg-[#0096d1] text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
              >
                Contact Us
              </Button>
              <a href="tel:4036044218">
                <Button 
                  variant="outline"
                  className="border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF] hover:text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Call 403-604-4218
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
